import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faColumns } from "@fortawesome/pro-solid-svg-icons/faColumns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SorterResult } from "antd/es/table/interface";
import Tooltip from "antd/es/tooltip";
import { FormattedMessage, useIntl } from "react-intl";

import IntegrationService from "@mapmycustomers/shared/enum/integrations/IntegrationService";
import IntegrationSyncDirection, {
  getSyncDirection,
} from "@mapmycustomers/shared/enum/integrations/IntegrationSyncDirection";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import Organization from "@mapmycustomers/shared/types/Organization";
import Sandbox from "@mapmycustomers/shared/types/Sandbox";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import GecodeLimitInfo from "@app/component/GecodeLimitInfo";
import { skipSyncArchiveFields } from "@app/component/SelectColumnsPane/util/fieldFilter";
import Table from "@app/component/Table";
import TextWithLine from "@app/component/typography/TextWithLine";
import configService from "@app/config/ConfigService";
import Environment from "@app/enum/Environment";
import { PAGE_LIMIT } from "@app/scene/settings/component/Integrations/utils/consts";
import { downloadFile } from "@app/store/files/actions";
import { getOrganization, isOnTrialPlan } from "@app/store/iam";
import {
  deleteIntegration,
  initializeHomePage,
  updateIntegrationSyncsViewState,
} from "@app/store/integrations/actions";
import {
  getCurrentIntegration,
  getIntegrationSyncs,
  getIntegrationViewState,
  getLastIntegrationSync,
  getLoading,
  getLoadingSyncs,
  getLoadingSyncsTotal,
} from "@app/store/integrations/selectors";
import SUPPORTED_SERVICES from "@app/store/integrations/supported-services";
import { RootState } from "@app/store/rootReducer";
import Integration from "@app/types/integrations/Integration";
import IntegrationSync from "@app/types/integrations/IntegrationSync";
import IntegrationSyncViewState from "@app/types/viewModel/IntegrationSyncsViewState";
import getContactWithIntegrationTeamLink from "@app/util/integrations/getContactWithIntegrationTeamLink";

import isSandboxCrmValid from "../../util/isSandboxCrmValid";
import SandboxIntegrationReAuth from "../common/SandboxIntegrationReAuth";
import SettingPage from "../common/SettingPage";

import IntegrationDetails from "./components/IntegrationDetails";
import IntegrationDetailsHeader from "./components/IntegrationDetailsHeader";
import integrationAnalytics from "./integrationAnalytics";
import styles from "./Integrations.module.scss";
import IntegrationTimeSelector from "./IntegrationTimeSelector";
import NoSyncIntegration from "./NoSyncIntegration";
import SelectColumnsPane from "./SelectColumnsPane";
import StatusFilter from "./StatusFilter";
import useIntegrationSyncsTableColumnConfig from "./utils/useIntegrationSyncsTableColumnConfig";

interface Props {
  currentIntegration?: Integration;
  deleteIntegration: typeof deleteIntegration.request;
  downloadFile: typeof downloadFile;
  initializeHomePage: typeof initializeHomePage.request;
  integrationSyncs: IntegrationSync[];
  isTrial?: boolean;
  lastIntegrationSync?: IntegrationSync;
  loading: boolean;
  organization?: Organization;
  saving: boolean;
  syncsLoading: boolean;
  syncsTotal: number;
  updateIntegrationSyncsViewState: typeof updateIntegrationSyncsViewState.request;
  viewState: IntegrationSyncViewState;
}

const Integrations: React.FC<Props> = ({
  currentIntegration,
  deleteIntegration,
  downloadFile,
  initializeHomePage,
  integrationSyncs,
  isTrial,
  lastIntegrationSync,
  loading,
  organization,
  syncsLoading,
  syncsTotal,
  updateIntegrationSyncsViewState,
  viewState,
}) => {
  const intl = useIntl();
  const currentSandbox: Sandbox | undefined = organization?.metaData?.connectedOrg;
  const isOnSandbox = configService.getCurrentEnvironment() === Environment.SANDBOX;
  const [selectColumnVisible, showSelectColumn, hideSelectColumn] = useBoolean();
  const [page, setPage] = useState<number>(1);
  const [syncDirection, setSyncDirection] = useState<IntegrationSyncDirection | undefined>(
    undefined
  );
  useEffect(() => {
    if (!currentIntegration) {
      return;
    }

    const usedSyncDirections = new Set(
      Object.values(currentIntegration.syncOptions).map(getSyncDirection)
    );

    let directionToReport: string;
    // selecting a highest direction to report
    if (
      usedSyncDirections?.has(IntegrationSyncDirection.BOTH_DIRECTIONS) ||
      (usedSyncDirections?.has(IntegrationSyncDirection.SERVICE_TO_MMC) &&
        usedSyncDirections?.has(IntegrationSyncDirection.MMC_TO_SERVICE))
    ) {
      directionToReport = "bidirectional";
      setSyncDirection(IntegrationSyncDirection.BOTH_DIRECTIONS);
    } else if (usedSyncDirections?.has(IntegrationSyncDirection.SERVICE_TO_MMC)) {
      directionToReport = "incoming";
      setSyncDirection(IntegrationSyncDirection.SERVICE_TO_MMC);
    } else if (usedSyncDirections?.has(IntegrationSyncDirection.MMC_TO_SERVICE)) {
      directionToReport = "outgoing";
      setSyncDirection(IntegrationSyncDirection.MMC_TO_SERVICE);
    } else {
      return;
    }

    const integrationType = currentIntegration?.type;
    const integrationSyncDirection = {
      dynamics: integrationType === IntegrationService.DYNAMICS ? directionToReport : "off",
      hubspot: integrationType === IntegrationService.HUBSPOT ? directionToReport : "off",
      salesforce: integrationType === IntegrationService.SALESFORCE ? directionToReport : "off",
      zoho: integrationType === IntegrationService.ZOHO ? directionToReport : "off",
    };
    integrationAnalytics.using([], { ...integrationSyncDirection });
  }, [currentIntegration]);

  useEffect(() => {
    initializeHomePage();
  }, [initializeHomePage]);

  const serviceDescription = useMemo(
    () =>
      SUPPORTED_SERVICES.find(
        ({ type }) => type === (currentIntegration?.type ?? currentSandbox?.crm)
      ),
    [currentIntegration?.type, currentSandbox?.crm]
  );

  const handleAskAboutAdvancedSyncs = useCallback(
    () => integrationAnalytics.clicked(["Ask about Advanced Syncs"]),
    []
  );

  const integrationSyncsTableColumnConfig = useIntegrationSyncsTableColumnConfig(
    intl,
    downloadFile,
    viewState.columns,
    syncDirection,
    viewState.sort,
    viewState.sortOrder
  );

  const handleChangeTable = useCallback(
    (
      pagination,
      filters,
      sorting: SorterResult<IntegrationSync> | SorterResult<IntegrationSync>[]
    ) => {
      const stateToUpdate: Partial<IntegrationSyncViewState> = { ...viewState };
      if (!Array.isArray(sorting) && sorting.columnKey) {
        stateToUpdate.sort = (sorting as SorterResult<IntegrationSync>).columnKey as string;
        stateToUpdate.sortOrder = sorting.order === "descend" ? SortOrder.DESC : SortOrder.ASC;
      }

      updateIntegrationSyncsViewState({
        page: pagination.current,
        viewState: stateToUpdate,
      });
      setPage(pagination.current);
    },
    [setPage, updateIntegrationSyncsViewState, viewState]
  );

  if (
    (!currentIntegration || !serviceDescription) &&
    !loading &&
    (!isSandboxCrmValid(currentSandbox?.crm) ||
      !isOnSandbox ||
      !currentSandbox?.config?.integrations)
  ) {
    return <NoSyncIntegration />;
  }

  return (
    <SettingPage
      actions={
        <Button
          href={getContactWithIntegrationTeamLink(isTrial ?? false)}
          onClick={handleAskAboutAdvancedSyncs}
          target="_blank"
          type="link"
        >
          <FormattedMessage
            id="integrations.home.header.action"
            defaultMessage="Ask about Advanced Syncs"
            description="Integration home action"
          />
        </Button>
      }
      pageContentClassName={styles.pageContent}
      subTitle={
        <div>
          <div>
            <GecodeLimitInfo
              noLimitReachedTitle={intl.formatMessage({
                id: "integrations.home.geocodingLimit.title",
                defaultMessage: "Moving large amounts of new data with your integration?",
                description: "Integration home page - no limit title",
              })}
            />
          </div>
          <div>
            {intl.formatMessage({
              id: "integrations.home.information",
              defaultMessage: "Access, review and quickly modify your integration",
              description: "Integration home information",
            })}
          </div>
        </div>
      }
      title={intl.formatMessage({
        id: "integrations.home.title",
        defaultMessage: "Integration",
        description: "Title of integration home page",
      })}
    >
      {loading ? (
        <LoadingSpinner className={styles.loadingSpinner} />
      ) : (
        <>
          <IntegrationDetails
            header={
              <IntegrationDetailsHeader
                integrationEnvType={currentIntegration?.envType}
                integrationId={currentIntegration?.id}
                integrationPortal={currentIntegration?.metadata}
                integrationSchedule={currentIntegration?.schedule}
                integrationType={currentIntegration?.type ?? currentSandbox?.crm}
                onDeleteIntegration={deleteIntegration}
              />
            }
            integrationSync={lastIntegrationSync}
            integrationSyncOptions={currentIntegration?.syncOptions}
            integrationType={currentIntegration?.type ?? currentSandbox?.crm}
            isRealIntegration={!!currentIntegration?.id}
            selectedService={serviceDescription}
          />
          <div className={styles.blockDivider} />
          {currentIntegration?.id && (
            <div>
              <TextWithLine className={styles.Header}>
                <div className={styles.syncTitle}>
                  <FormattedMessage
                    id="integrations.home.syncArchive"
                    defaultMessage="Sync archive"
                    description="Integration home sync archive"
                  />
                  <IntegrationTimeSelector />
                </div>
                <div className={styles.filters}>
                  <StatusFilter />
                  <Tooltip
                    placement="bottom"
                    title={intl.formatMessage({
                      id: "settings.integration.hide.show.option",
                      defaultMessage: "Hide/Show columns",
                      description: "Hide show option for Integrations",
                    })}
                  >
                    <Button
                      className={styles.columnsIcon}
                      icon={<FontAwesomeIcon icon={faColumns} />}
                      iconOnly
                      onClick={showSelectColumn}
                      type="text"
                    />
                  </Tooltip>
                </div>
              </TextWithLine>
            </div>
          )}
          {syncsLoading && <LoadingSpinner />}
          {currentIntegration?.id && !syncsLoading && (
            <Table<IntegrationSync>
              className={styles.syncsTable}
              columns={integrationSyncsTableColumnConfig}
              dataSource={integrationSyncs}
              noHover
              onChange={handleChangeTable}
              pagination={{
                current: page,
                pageSize: PAGE_LIMIT,
                position: ["bottomCenter"],
                showSizeChanger: false,
                total: syncsTotal,
              }}
              rowKey="id"
              scrollable
              size="middle"
            />
          )}
          {!currentIntegration?.id && <SandboxIntegrationReAuth showActions={false} />}
        </>
      )}
      <SelectColumnsPane
        fieldFilter={skipSyncArchiveFields(syncDirection)}
        onHide={hideSelectColumn}
        visible={selectColumnVisible}
      />
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentIntegration: getCurrentIntegration(state),
  integrationSyncs: getIntegrationSyncs(state),
  isTrial: isOnTrialPlan(state),
  lastIntegrationSync: getLastIntegrationSync(state),
  loading: getLoading(state),
  organization: getOrganization(state),
  syncsLoading: getLoadingSyncs(state),
  syncsTotal: getLoadingSyncsTotal(state),
  viewState: getIntegrationViewState(state),
});

const mapDispatchToProps = {
  deleteIntegration: deleteIntegration.request,
  downloadFile,
  initializeHomePage: initializeHomePage.request,
  updateIntegrationSyncsViewState: updateIntegrationSyncsViewState.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Integrations);
